import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, MODAL_BREDDE, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { bookCatalog } from '@/data/bookCatalog';
import {
  fjernOverlay,
  lagreOverlay,
  lesOverlay,
  type BokOverlay,
} from '@/data/bokOverlay';
import { alleSjangre } from '@/data/kritikere';
import type { Author, AuthorStats } from '@/data/types';
import type { Tier } from '@/state/GameState';
import { animateModalGlide } from '@/ui/modalGlide';
import { ToastManager } from '@/ui/Toast';

const SHEET_WIDTH = MODAL_BREDDE;
const SHEET_PADDING = 24;
const SHEET_TOP = 40;
const TIER_ORDER: ReadonlyArray<Tier> = ['D', 'C', 'B', 'A', 'S'];
const STAT_KEYS: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

type Fane = 'boker' | 'forfattere';

export class AdminModal {
  private layer?: Phaser.GameObjects.Container;
  private innholdsContainer?: Phaser.GameObjects.Container;
  private maske?: Phaser.Display.Masks.GeometryMask;
  private wheelHandler?: (_p: unknown, _g: unknown, _dx: number, dy: number) => void;
  private scrollY = 0;
  private scrollMax = 0;
  private aktivFane: Fane = 'boker';
  private toast?: ToastManager;
  private bokEndringer: Map<string, BokOverlay> = new Map();
  private innholdsBaseY = 0;
  private innholdsBaseX = 0;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  setToastManager(t: ToastManager): void {
    this.toast = t;
  }

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(180);
    this.bokEndringer = new Map();
    for (const o of lesOverlay()) {
      this.bokEndringer.set(o.isbn, { ...o });
    }

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.74)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    this.render();
    animateModalGlide(this.scene, this.layer);
  }

  close(): void {
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    this.layer?.destroy(true);
    this.layer = undefined;
    this.innholdsContainer = undefined;
    this.maske = undefined;
    this.scrollY = 0;
    this.scrollMax = 0;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetH = GAME_HEIGHT - SHEET_TOP * 2;

    const sheet = this.scene.add
      .rectangle(sheetX, SHEET_TOP, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.7);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, SHEET_TOP, SHEET_WIDTH, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    this.byggHeader(sheetX, SHEET_TOP);

    const advarselY = SHEET_TOP + 96;
    this.byggAdvarsel(sheetX, advarselY);

    const faneY = advarselY + 56;
    this.byggFaner(sheetX, faneY);

    const innholdY = faneY + 60;
    const innholdH = sheetH - (innholdY - SHEET_TOP) - 24;
    this.byggScrollOmraade(sheetX, innholdY, innholdH);
  }

  private byggHeader(sheetX: number, sheetY: number): void {
    if (!this.layer) return;
    const tittel = this.scene.add
      .text(sheetX + SHEET_PADDING, sheetY + 28, 'Admin-modus', {
        fontFamily: FONTS.display,
        fontSize: fontStr(44),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const undertittel = this.scene.add
      .text(
        sheetX + SHEET_PADDING,
        sheetY + 72,
        'Rediger bok-katalog og forfatter-data',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#3a2e22',
          fontStyle: 'italic',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertittel);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - SHEET_PADDING, sheetY + 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);
  }

  private byggAdvarsel(sheetX: number, y: number): void {
    if (!this.layer) return;
    const w = SHEET_WIDTH - SHEET_PADDING * 2;
    const h = 44;
    const bg = this.scene.add
      .rectangle(sheetX + SHEET_PADDING, y, w, h, 0xf4d58d, 0.8)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.6);
    this.layer.add(bg);

    const tekst = this.scene.add
      .text(
        sheetX + SHEET_PADDING + 16,
        y + h / 2,
        '⚠  Admin-modus: endringer påvirker spillet umiddelbart',
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#1a1a1a',
          fontStyle: '700',
        },
      )
      .setOrigin(0, 0.5);
    this.layer.add(tekst);
  }

  private byggFaner(sheetX: number, y: number): void {
    if (!this.layer) return;
    const totalBredde = SHEET_WIDTH - SHEET_PADDING * 2;
    const faneBredde = totalBredde / 2;

    const faner: Array<{ id: Fane; navn: string }> = [
      { id: 'boker', navn: 'Bok-katalog' },
      { id: 'forfattere', navn: 'Mine forfattere' },
    ];

    for (let i = 0; i < faner.length; i += 1) {
      const fane = faner[i];
      const fx = sheetX + SHEET_PADDING + i * faneBredde;
      const aktiv = this.aktivFane === fane.id;

      const bg = this.scene.add
        .rectangle(
          fx,
          y,
          faneBredde,
          48,
          aktiv ? COLORS.wine : COLORS.white,
          1,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.wine, 0.6);
      this.layer.add(bg);

      const tekst = this.scene.add
        .text(fx + faneBredde / 2, y + 24, fane.navn, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: aktiv ? '#ffffff' : '#862736',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      this.layer.add(tekst);

      if (!aktiv) {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerup', () => {
          this.aktivFane = fane.id;
          this.scrollY = 0;
          this.render();
        });
      }
    }
  }

  private byggScrollOmraade(sheetX: number, y: number, h: number): void {
    if (!this.layer) return;
    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(sheetX + SHEET_PADDING, y, SHEET_WIDTH - SHEET_PADDING * 2, h);
    this.maske = maskShape.createGeometryMask();

    this.innholdsBaseX = sheetX + SHEET_PADDING;
    this.innholdsBaseY = y;
    const innhold = this.scene.add.container(this.innholdsBaseX, this.innholdsBaseY - this.scrollY);
    innhold.setMask(this.maske);
    this.layer.add(innhold);
    this.innholdsContainer = innhold;

    let cursorY = 0;
    if (this.aktivFane === 'boker') {
      cursorY = this.byggBokListe(innhold, SHEET_WIDTH - SHEET_PADDING * 2, 0);
    } else {
      cursorY = this.byggForfatterListe(innhold, SHEET_WIDTH - SHEET_PADDING * 2, 0);
    }

    this.scrollMax = Math.max(0, cursorY - h + 40);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
    innhold.y = this.innholdsBaseY - this.scrollY;

    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
    }
    this.wheelHandler = (_p, _g, _dx, dy) => {
      if (!this.isOpen) return;
      this.scrollBy(dy);
    };
    this.scene.input.on('wheel', this.wheelHandler);
  }

  private scrollBy(dy: number): void {
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    if (this.innholdsContainer) {
      this.innholdsContainer.y = this.innholdsBaseY - this.scrollY;
    }
  }

  private byggBokListe(
    layer: Phaser.GameObjects.Container,
    bredde: number,
    startY: number,
  ): number {
    let y = startY;

    const knappRad = this.byggBokKnapper(layer, bredde, y);
    y = knappRad + 24;

    const boker = [...bookCatalog.getAll()].sort((a, b) =>
      a.title.localeCompare(b.title, 'no'),
    );

    if (boker.length === 0) {
      const tom = this.scene.add
        .text(0, y, 'Ingen bøker lastet inn ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#7a7060',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tom);
      return y + 40;
    }

    for (const bok of boker) {
      y = this.byggBokKort(layer, bredde, y, bok.isbn, bok.title, bok.author, bok.genre, bok.tier);
      y += 12;
    }

    return y;
  }

  private byggBokKnapper(
    layer: Phaser.GameObjects.Container,
    bredde: number,
    y: number,
  ): number {
    const knappH = 44;
    const knappW = 220;

    const lagre = this.scene.add
      .rectangle(0, y, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 1);
    const lagreT = this.scene.add
      .text(knappW / 2, y + knappH / 2, 'Lagre endringer', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add([lagre, lagreT]);
    lagre.setInteractive({ useHandCursor: true });
    lagre.on('pointerup', () => this.lagreBokEndringer());

    const tilbakestill = this.scene.add
      .rectangle(knappW + 12, y, knappW, knappH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.6);
    const tilbakestillT = this.scene.add
      .text(knappW + 12 + knappW / 2, y + knappH / 2, 'Tilbakestill alle', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add([tilbakestill, tilbakestillT]);
    tilbakestill.setInteractive({ useHandCursor: true });
    tilbakestill.on('pointerup', () => this.bekreftTilbakestill());

    const antall = this.bokEndringer.size;
    const status = this.scene.add
      .text(
        knappW * 2 + 24,
        y + knappH / 2,
        antall > 0 ? `${antall} overstyring${antall === 1 ? '' : 'er'} aktiv` : 'Ingen aktive overstyringer',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(15),
          color: '#3a2e22',
          fontStyle: 'italic',
        },
      )
      .setOrigin(0, 0.5);
    layer.add(status);

    void bredde;
    return y + knappH;
  }

  private byggBokKort(
    layer: Phaser.GameObjects.Container,
    bredde: number,
    y: number,
    isbn: string,
    tittel: string,
    forfatter: string,
    sjanger: string,
    tier: Tier,
  ): number {
    const kortH = 132;
    const endring = this.bokEndringer.get(isbn);
    const harEndring = !!endring;

    const bg = this.scene.add
      .rectangle(
        0,
        y,
        bredde,
        kortH,
        harEndring ? 0xfff4d8 : COLORS.white,
        1,
      )
      .setOrigin(0, 0)
      .setStrokeStyle(harEndring ? 2 : 1, harEndring ? COLORS.ochre : COLORS.oakDark, 0.6);
    layer.add(bg);

    const tittelT = this.scene.add
      .text(12, y + 12, tittel, {
        fontFamily: FONTS.display,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        wordWrap: { width: bredde - 200 },
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    tittelT.on('pointerup', () => this.redigerBokFelt(isbn, 'tittel', tittel));
    layer.add(tittelT);

    const isbnT = this.scene.add
      .text(bredde - 12, y + 16, `ISBN ${isbn}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: '#7a7060',
      })
      .setOrigin(1, 0);
    layer.add(isbnT);

    const forfatterT = this.scene.add
      .text(12, y + 50, `Forfatter: ${forfatter}`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(15),
        color: '#3a2e22',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    forfatterT.on('pointerup', () => this.redigerBokFelt(isbn, 'author', forfatter));
    layer.add(forfatterT);

    const sjangerT = this.scene.add
      .text(12, y + 78, `Sjanger: ${sjanger}`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(15),
        color: '#3a2e22',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    sjangerT.on('pointerup', () => this.redigerBokSjanger(isbn, sjanger));
    layer.add(sjangerT);

    const tierX = bredde - 12;
    const tierT = this.scene.add
      .text(tierX, y + 50, `Tier: ${tier}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    tierT.on('pointerup', () => this.redigerBokTier(isbn, tier));
    layer.add(tierT);

    const hint = this.scene.add
      .text(12, y + kortH - 22, 'Klikk på tittel, forfatter, sjanger eller tier for å endre.', {
        fontFamily: FONTS.body,
        fontSize: fontStr(12),
        color: '#9a9285',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    layer.add(hint);

    if (harEndring) {
      const fjern = this.scene.add
        .text(bredde - 12, y + kortH - 22, '× Fjern endringer', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(13),
          color: '#862736',
          fontStyle: '700',
        })
        .setOrigin(1, 0)
        .setInteractive({ useHandCursor: true });
      fjern.on('pointerup', () => {
        this.bokEndringer.delete(isbn);
        lagreOverlay([...this.bokEndringer.values()]);
        bookCatalog.reapplyOverlay();
        this.render();
      });
      layer.add(fjern);
    }

    return y + kortH;
  }

  private redigerBokFelt(
    isbn: string,
    felt: 'tittel' | 'author',
    nogjeldende: string,
  ): void {
    if (typeof window === 'undefined') return;
    const ledetekst =
      felt === 'tittel' ? 'Ny tittel for boken:' : 'Nytt forfatternavn:';
    const ny = window.prompt(ledetekst, nogjeldende);
    if (ny === null) return;
    const trimmet = ny.trim();
    if (trimmet.length === 0) return;
    if (trimmet === nogjeldende) return;
    const eksisterende = this.bokEndringer.get(isbn) ?? { isbn };
    if (felt === 'tittel') eksisterende.tittel = trimmet;
    else eksisterende.author = trimmet;
    this.bokEndringer.set(isbn, eksisterende);
    this.render();
  }

  private redigerBokSjanger(isbn: string, nogjeldende: string): void {
    if (typeof window === 'undefined') return;
    const sjangre = alleSjangre();
    const liste = sjangre.join(', ');
    const ny = window.prompt(`Sjanger (valgmuligheter: ${liste}):`, nogjeldende);
    if (ny === null) return;
    const trimmet = ny.trim().toUpperCase();
    if (trimmet.length === 0) return;
    if (!sjangre.includes(trimmet)) {
      this.toast?.vis(`Ugyldig sjanger: ${trimmet}`);
      return;
    }
    const eksisterende = this.bokEndringer.get(isbn) ?? { isbn };
    eksisterende.genre = trimmet;
    this.bokEndringer.set(isbn, eksisterende);
    this.render();
  }

  private redigerBokTier(isbn: string, nogjeldende: Tier): void {
    if (typeof window === 'undefined') return;
    const ny = window.prompt(
      `Tier (D, C, B, A, S):`,
      nogjeldende,
    );
    if (ny === null) return;
    const opp = ny.trim().toUpperCase() as Tier;
    if (!TIER_ORDER.includes(opp)) {
      this.toast?.vis(`Ugyldig tier: ${ny}`);
      return;
    }
    const eksisterende = this.bokEndringer.get(isbn) ?? { isbn };
    eksisterende.tier = opp;
    this.bokEndringer.set(isbn, eksisterende);
    this.render();
  }

  private lagreBokEndringer(): void {
    const overlay = [...this.bokEndringer.values()];
    lagreOverlay(overlay);
    bookCatalog.reapplyOverlay();
    this.toast?.vis('Endringer lagret');
    this.render();
  }

  private bekreftTilbakestill(): void {
    if (typeof window === 'undefined') return;
    const ok = window.confirm(
      'Er du sikker? Dette fjerner alle overstyringer av bok-katalogen.',
    );
    if (!ok) return;
    this.bokEndringer.clear();
    fjernOverlay();
    bookCatalog.reapplyOverlay();
    this.toast?.vis('Overstyringer tilbakestilt');
    this.render();
  }

  private byggForfatterListe(
    layer: Phaser.GameObjects.Container,
    bredde: number,
    startY: number,
  ): number {
    let y = startY;
    const authors = Object.values(gameState.authors).sort((a, b) =>
      a.navn.localeCompare(b.navn, 'no'),
    );

    if (authors.length === 0) {
      const tom = this.scene.add
        .text(0, y, 'Ingen forfattere ennå — kontraher en bok først.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#7a7060',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tom);
      return y + 40;
    }

    for (const a of authors) {
      y = this.byggForfatterKort(layer, bredde, y, a);
      y += 14;
    }

    return y;
  }

  private byggForfatterKort(
    layer: Phaser.GameObjects.Container,
    bredde: number,
    y: number,
    forfatter: Author,
  ): number {
    const kortH = 360;
    const bg = this.scene.add
      .rectangle(0, y, bredde, kortH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.5);
    layer.add(bg);

    const navnT = this.scene.add
      .text(12, y + 12, forfatter.navn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#862736',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    navnT.on('pointerup', () => this.redigerForfatterNavn(forfatter.id, forfatter.navn));
    layer.add(navnT);

    const idT = this.scene.add
      .text(bredde - 12, y + 18, `id: ${forfatter.id}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(12),
        color: '#9a9285',
      })
      .setOrigin(1, 0);
    layer.add(idT);

    const sjangerT = this.scene.add
      .text(12, y + 48, `Sjanger: ${forfatter.primaerSjanger}`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(15),
        color: '#3a2e22',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    sjangerT.on('pointerup', () =>
      this.redigerForfatterSjanger(forfatter.id, forfatter.primaerSjanger),
    );
    layer.add(sjangerT);

    const pseudonymT = this.scene.add
      .text(
        bredde - 12,
        y + 48,
        forfatter.pseudonym ? 'Pseudonym: ja' : 'Pseudonym: nei',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(14),
          color: '#3a2e22',
        },
      )
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    pseudonymT.on('pointerup', () => {
      gameState.oppdaterForfatter(forfatter.id, { pseudonym: !forfatter.pseudonym });
      this.toast?.vis('Pseudonym oppdatert');
      this.render();
    });
    layer.add(pseudonymT);

    let statY = y + 80;
    const statBredde = bredde - 24;
    const statKolBredde = (statBredde - 16) / 2;
    for (let i = 0; i < STAT_KEYS.length; i += 1) {
      const key = STAT_KEYS[i];
      const kol = i % 2;
      const rad = Math.floor(i / 2);
      const sx = 12 + kol * (statKolBredde + 16);
      const sy = statY + rad * 36;
      this.byggStatRad(
        layer,
        sx,
        sy,
        statKolBredde,
        key,
        forfatter.stats[key],
        forfatter.id,
      );
    }
    statY += Math.ceil(STAT_KEYS.length / 2) * 36 + 8;

    const meta: Array<{ label: string; verdi: number; felt: keyof Author }> = [
      { label: 'Lojalitet', verdi: forfatter.lojalitet, felt: 'lojalitet' },
      { label: 'Stress', verdi: forfatter.stress, felt: 'stress' },
      { label: 'Ego', verdi: forfatter.ego, felt: 'ego' },
      { label: 'Drikkenivå', verdi: forfatter.drikkenivaa, felt: 'drikkenivaa' },
    ];
    for (let i = 0; i < meta.length; i += 1) {
      const m = meta[i];
      const kol = i % 2;
      const rad = Math.floor(i / 2);
      const sx = 12 + kol * (statKolBredde + 16);
      const sy = statY + rad * 36;
      this.byggMetaRad(layer, sx, sy, statKolBredde, m.label, m.verdi, forfatter.id, m.felt);
    }

    return y + kortH;
  }

  private byggStatRad(
    layer: Phaser.GameObjects.Container,
    x: number,
    y: number,
    bredde: number,
    key: keyof AuthorStats,
    verdi: number,
    authorId: string,
  ): void {
    const label = this.scene.add
      .text(x, y, capitalize(key), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: '#3a2e22',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    layer.add(label);

    const barX = x + 110;
    const barW = bredde - 160;
    const barH = 16;
    const barY = y + 4;

    const ramme = this.scene.add
      .rectangle(barX, barY, barW, barH, 0xeae0c8, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.5);
    layer.add(ramme);

    const fill = this.scene.add
      .rectangle(barX, barY, Math.max(1, (barW * verdi) / 100), barH, COLORS.wine, 1)
      .setOrigin(0, 0);
    layer.add(fill);

    const verdiT = this.scene.add
      .text(x + bredde, y, String(verdi), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    verdiT.on('pointerup', () => this.redigerStat(authorId, key, verdi));
    layer.add(verdiT);
  }

  private byggMetaRad(
    layer: Phaser.GameObjects.Container,
    x: number,
    y: number,
    bredde: number,
    label: string,
    verdi: number,
    authorId: string,
    felt: keyof Author,
  ): void {
    const labelT = this.scene.add
      .text(x, y, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: '#3a2e22',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    layer.add(labelT);

    const barX = x + 110;
    const barW = bredde - 160;
    const barH = 16;
    const barY = y + 4;
    const ramme = this.scene.add
      .rectangle(barX, barY, barW, barH, 0xeae0c8, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.5);
    layer.add(ramme);
    const fill = this.scene.add
      .rectangle(barX, barY, Math.max(1, (barW * verdi) / 100), barH, COLORS.forest, 1)
      .setOrigin(0, 0);
    layer.add(fill);

    const verdiT = this.scene.add
      .text(x + bredde, y, String(verdi), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    verdiT.on('pointerup', () => this.redigerMetaVerdi(authorId, felt, verdi));
    layer.add(verdiT);
  }

  private redigerForfatterNavn(id: string, nogjeldende: string): void {
    if (typeof window === 'undefined') return;
    const ny = window.prompt('Nytt forfatternavn:', nogjeldende);
    if (ny === null) return;
    const trimmet = ny.trim();
    if (trimmet.length === 0) return;
    if (trimmet === nogjeldende) return;
    const ok = gameState.oppdaterForfatter(id, { navn: trimmet });
    if (ok) {
      this.toast?.vis('Forfatter oppdatert');
      this.render();
    }
  }

  private redigerForfatterSjanger(id: string, nogjeldende: string): void {
    if (typeof window === 'undefined') return;
    const sjangre = alleSjangre();
    const ny = window.prompt(
      `Primær-sjanger (${sjangre.join(', ')}):`,
      nogjeldende,
    );
    if (ny === null) return;
    const opp = ny.trim().toUpperCase();
    if (!sjangre.includes(opp)) {
      this.toast?.vis(`Ugyldig sjanger: ${ny}`);
      return;
    }
    const ok = gameState.oppdaterForfatter(id, { primaerSjanger: opp });
    if (ok) {
      this.toast?.vis('Sjanger oppdatert');
      this.render();
    }
  }

  private redigerStat(
    authorId: string,
    key: keyof AuthorStats,
    nogjeldende: number,
  ): void {
    if (typeof window === 'undefined') return;
    const ny = window.prompt(`${capitalize(key)} (0-100):`, String(nogjeldende));
    if (ny === null) return;
    const num = Number(ny.trim());
    if (!Number.isFinite(num)) {
      this.toast?.vis('Ugyldig tall');
      return;
    }
    const eksisterende = gameState.authors[authorId];
    if (!eksisterende) return;
    const nyStats: AuthorStats = { ...eksisterende.stats, [key]: num };
    const ok = gameState.oppdaterForfatter(authorId, { stats: nyStats });
    if (ok) {
      this.toast?.vis(`${capitalize(key)} oppdatert`);
      this.render();
    }
  }

  private redigerMetaVerdi(
    authorId: string,
    felt: keyof Author,
    nogjeldende: number,
  ): void {
    if (typeof window === 'undefined') return;
    const ny = window.prompt(`${String(felt)} (0-100):`, String(nogjeldende));
    if (ny === null) return;
    const num = Number(ny.trim());
    if (!Number.isFinite(num)) {
      this.toast?.vis('Ugyldig tall');
      return;
    }
    const endringer: Partial<Author> = {};
    if (felt === 'lojalitet') endringer.lojalitet = num;
    else if (felt === 'stress') endringer.stress = num;
    else if (felt === 'ego') endringer.ego = num;
    else if (felt === 'drikkenivaa') endringer.drikkenivaa = num;
    else return;
    const ok = gameState.oppdaterForfatter(authorId, endringer);
    if (ok) {
      this.toast?.vis('Verdi oppdatert');
      this.render();
    }
  }
}

function capitalize(s: string): string {
  if (s.length === 0) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
